import { View, Text, Pressable, ActivityIndicator } from 'react-native'
import React, { useEffect, useState } from 'react'
import { StyleSheet } from 'react-native-unistyles'
import Unicon from '@/components/General/Unicon';
import CutCard from '@/components/General/CutCard';
import SheetModal from '@/components/General/SheetModal';
import usePlatform from '@/utils/hooks/usePlatform';

export default function KPICard({title, content, rightHeaderComponent, trendValue, trendDirection, details, detailsTitle = "Details", onDetailsOpen, detailsLoading = false, modalOpen: controlledModalOpen, onModalClose }) {
    const [internalModalOpen, setInternalModalOpen] = useState(false);
    const [DetailsTable, setDetailsTable] = useState(null);
    const { isNative } = usePlatform();
    const [availableHeight, setAvailableHeight] = useState(0);

    const isControlled = controlledModalOpen !== undefined;
    const detailsModalOpen = isControlled ? controlledModalOpen : internalModalOpen;
    const closeModal = isControlled ? onModalClose : () => setInternalModalOpen(false);

    useEffect(() => {
        if (detailsModalOpen && !DetailsTable) {
            import('@/components/Charts/KPICardDetailsTable').then((mod) => {
                setDetailsTable(() => mod.default);
            });
        }
    }, [detailsModalOpen, DetailsTable]);

    const hasDetails = (details && details.data && details.data.length > 0) || !!onDetailsOpen;

    const handleDetailsOpen = () => {
        if (!isControlled) {
            setInternalModalOpen(true);
        }
        if (onDetailsOpen) {
            onDetailsOpen();
        }
    };

    return (
        <CutCard style={styles.statCard} contentStyle={styles.statCardContent}>
            <Text style={styles.statTitle}>{title}</Text>
            <View style={styles.statValueContainer}>
                {typeof content == "number" || typeof content == "string" ? <Text style={styles.statValue}>{content}</Text> : content}
            </View>

            {(trendValue && trendDirection) &&
                <View style={styles.trendContainer}>
                    <View style={styles.trendIconContainer}>
                        <Unicon name={trendDirection == "up" ? "trending-up-outline" : "trending-down-outline"} size={16} colorVariant={trendDirection == "up" ? "success" : "error"}/>
                    </View>
                    <Text style={styles.trendValue(trendDirection)}>{trendDirection == "up" ? "+" : "-"} {trendValue}</Text>
                </View>
            }

            <View style={styles.rightHeaderContainer}>
                {rightHeaderComponent}
            </View>

            {hasDetails && (
                <Pressable onPress={handleDetailsOpen}>
                    <Text style={styles.details}>{detailsTitle}</Text>
                </Pressable>
            )}

            <SheetModal
                isOpen={detailsModalOpen}
                onCancel={closeModal}
                title={title}
                cancelText="Close"
            >
                <View style={styles.modalWrapper} onLayout={(e) => setAvailableHeight(e.nativeEvent.layout.height)}>
                    {detailsLoading || !DetailsTable ? (
                        <View style={styles.modalLoading}>
                            <ActivityIndicator />
                        </View>
                    ) : (
                        <View style={[styles.tableContainer, isNative && availableHeight > 0 && { flex: 0, height: availableHeight - 20 }]}>
                            <DetailsTable details={details} isOpen={detailsModalOpen} />
                        </View>
                    )}
                </View>
            </SheetModal>
        </CutCard>
    )
}

const styles = StyleSheet.create(({colors, fonts, shadows, roundness}) => ({
    statCard: {
        flex: 1,
        padding: 18,
        boxShadow: shadows.card,
        minWidth: 200,
        _web: {
            willChange: 'transform',
        },
    },
    statCardContent: {
        gap: 6,
    },
    details: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.primary,
        marginTop: 4,
    },
    statTitle: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
    },
    statValueContainer: {
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: 2,
    },
    statValue: {
        fontSize: 28,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    trendContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        alignSelf: 'flex-start',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: roundness.sm,
        backgroundColor: colors.background,
    },
    trendIconContainer: {
    },
    trendValue: (trendDirection) => ({
        fontSize: 13,
        fontFamily: fonts.heading,
        color: trendDirection == "up" ? colors.success : colors.error,
    }),
    rightHeaderContainer: {
        position:"absolute", 
        right:14, 
        top:14
    },
    modalWrapper: {
        flex: 1,
        gap: 10,
        paddingBottom: 20,
    },
    modalLoading: {
        flex: 1,
        paddingVertical: 40,
        alignItems: 'center',
    },
    tableContainer: {
        flex: 1,
        minHeight: 300,
    },
}))
